import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listForgottenUsers,
  purgeForgottenUser,
  type ForgottenUser,
} from "./forgottenUsersApi";
import {
  purgeErrorKey,
  retainedFromProblem,
  retainedKindKey,
  retainedLineKey,
  rowState,
  type Retained,
} from "./forgottenUsers";
import type { Tenant } from "./tenantsApi";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { formatDate } from "@web/lib/date";
import { Modal } from "@web/ui";

// "UNOHDETUT KÄYTTÄJÄT" — the rows ADR-0014 §5's erasure leaves behind, and the one act that removes one
// of them for good (owner's request 4.11.2026, task 77dd58bf).
//
// WHY THE OPERATOR AND NOT THE TENANT'S OWN PÄÄKÄYTTÄJÄ. The erasure keeps the `users` row deliberately —
// the audit chain stays linked, a certificate's verifiable identifier still resolves, and compliance
// evidence is retained pseudonymously — so deleting the row is a decision ABOUT that decision, taken
// against a statutory-retention question the customer cannot answer for itself. ADR-0023 §8 settled the
// same question the same way one level up, for a whole tenant: narrow the capability to the platform
// operator and refuse outright wherever a retention obligation exists. The customer's own user list no
// longer SHOWS these people at all since the same owner's decision, which is the other half of the
// request: a row whose Transit key is destroyed can only render as "Poistettu käyttäjä 3f2a9c11", and a
// directory of people is the wrong place for an identifier string.
//
// WHY THE READ IS BEHIND A CLICK. Not for the reason the roles section's is — this read writes NO audit
// row and discloses no name, because there is no name left to disclose (the keys are gone, and every
// person here renders as the pseudonym derived from their own id). The reason is the page's own: choosing
// a tenant is not choosing to look at the erasures somebody ran in it, and most visits never open this.
//
// THREE ROW STATES AND THE MIDDLE ONE IS THE POINT (see `rowState`):
//
//   * PURGEABLE — the server says so, having asked both halves of the condition itself. The console
//     branches on its answer rather than re-deriving it from `retained.length`, so the button and the
//     endpoint cannot come apart.
//   * HELD — something still holds this row. Usually Verso's own data that must be kept: the classes are
//     LISTED, with the row count and, where ADR-0020 §10 has stamped one, the day the obligation ends. Not
//     a disabled button and not "ei onnistu": the only thing an operator can do about this state is wait
//     for a retention clock or go and find out why a training record exists for a forgotten person, and
//     neither is possible without knowing which class holds the row. ONE member of that list is not data
//     at all — `shared_identity`, the realm account the erasure deliberately kept because another tenant
//     shares the subject (ADR-0018 §5 point 3), for which this row IS the sign-in denial. It gets a
//     sentence of its own rather than a retention line, because nothing about it is waiting for a date.
//   * ERASURE UNFINISHED — ADR-0014 §5.2. Deliberately NOT folded into "held": nothing is being kept about
//     this person, the erasure simply has not finished, and the remedy is the retry on the TENANT's own
//     user-administration page. An operator told "data has to be kept" here would go looking for an
//     obligation that does not exist.
//
// THE CONFIRMATION SAYS WHAT GOES AND WHAT DOES NOT, and the second half is the one that needs the care:
// the audit trail is NEITHER DELETED NOR BROKEN. `audit_event.actor_id`/`target_id` are ADR-0011 §3.1
// class B precisely so a chain row outlives the person it names, and this deletion APPENDS one more row to
// the tenant's own chain. A confirmation that left that unsaid would invite the one objection this whole
// capability has to be able to answer.
//
// THE LIST IS RE-READ AFTER A DELETION rather than patched: the row is gone and the server's own list is
// what proves it. A refusal leaves the list exactly as it was — nothing was deleted — and shows the
// classes the refusal named, which may be newer than the ones the list was read with.

/** Where this section is. */
type Phase =
  /** Nothing read yet: the section offers the read and says what it is. */
  | { kind: "closed" }
  | { kind: "loading" }
  | { kind: "loadError" }
  | { kind: "open" }
  /** The confirmation is up for one person. */
  | { kind: "confirm"; person: ForgottenUser }
  /** The deletion is in flight — the dialog may not be taken away under it. */
  | { kind: "purging"; person: ForgottenUser };

/**
 * What the confirmation lists, as i18n keys. A list and not one paragraph: an operator reading a
 * confirmation of an irreversible act reads the bullets, and the two that matter most are the one naming
 * what goes WITH the row and the one saying the audit trail does not.
 */
const PURGE_EFFECTS = [
  "tenants.forgotten.effectRow",
  "tenants.forgotten.effectCascade",
  "tenants.forgotten.effectAudit",
  "tenants.forgotten.effectKey",
  "tenants.forgotten.effectIrreversible",
] as const;

export function TenantForgottenUsersSection({
  tenant,
  headingId,
  onCount,
  onDone,
}: {
  tenant: Tenant;
  /** The id of this section's own <h2> — the page owns the heading, this owns everything under it. */
  headingId: string;
  /**
   * The people this section has read, once it has. `undefined` means it has not been opened, and the nav
   * is NOT told a number it does not know — nor is this a reason to perform the read.
   */
  onCount: (people: ForgottenUser[] | undefined) => void;
  /** A completed deletion, for the page's banner. */
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [people, setPeople] = useState<ForgottenUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** The classes a REFUSAL named, which can be newer than the list's own. */
  const [refused, setRefused] = useState<Retained[]>([]);

  const openRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  /** Aborts the read if the operator leaves before it lands. */
  const readRef = useRef<AbortController | null>(null);

  useEffect(() => () => readRef.current?.abort(), []);

  const read = useCallback(
    ({ keepError = false }: { keepError?: boolean } = {}) => {
      if (!keepError) {
        setError(null);
        setRefused([]);
      }
      setPhase({ kind: "loading" });
      readRef.current?.abort();
      const controller = new AbortController();
      readRef.current = controller;

      listForgottenUsers(tenant.id, controller.signal)
        .then((rows) => {
          if (controller.signal.aborted) return;
          setPeople(rows);
          onCount(rows);
          setPhase({ kind: "open" });
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError || controller.signal.aborted) return;
          // The nav is told nothing: a failed read establishes no count, and `undefined` is the one
          // honest answer for it.
          onCount(undefined);
          setPhase({ kind: "loadError" });
        });
    },
    [tenant.id, onCount],
  );

  const onPurge = useCallback(
    (person: ForgottenUser) => {
      setPhase({ kind: "purging", person });
      setError(null);
      setRefused([]);

      purgeForgottenUser(tenant.id, person.userId)
        .then(() => {
          onDone(t("tenants.forgotten.done", { person: person.pseudonym }));
          // The list and not a patched array: the row is gone, and the server's answer is what says so.
          read();
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          if (err instanceof ApiError) {
            setError(t(purgeErrorKey(err.status, err.problem)));
            setRefused(retainedFromProblem(err.problem));
          } else {
            setError(t("tenants.forgotten.error"));
          }
          // Nothing was deleted, so the list still describes the tenant — but the refusal may have named
          // classes the list was read before, so it is re-read with the message kept.
          read({ keepError: true });
        });
    },
    [tenant.id, onDone, read, t],
  );

  return (
    <>
      <h2 id={headingId}>{t("tenants.forgotten.sectionTitle")}</h2>
      <p className="freeze__intro">{t("tenants.forgotten.intro")}</p>

      {phase.kind === "closed" && (
        <div className="tenant-section__actions">
          <button
            type="button"
            className="btn"
            ref={openRef}
            onClick={() => read()}
            data-testid="tenant-forgotten-open"
          >
            {t("tenants.forgotten.open")}
          </button>
        </div>
      )}

      {phase.kind === "loading" && (
        <p className="tenants__slug-status" data-testid="tenant-forgotten-loading">
          {t("tenants.forgotten.loading")}
        </p>
      )}

      {phase.kind === "loadError" && (
        <>
          <p className="banner banner--error" role="alert" data-testid="tenant-forgotten-load-error">
            {t("tenants.forgotten.loadError")}
          </p>
          <div className="tenant-section__actions">
            <button type="button" className="btn" onClick={() => read()}>
              {t("tenants.forgotten.retryRead")}
            </button>
          </div>
        </>
      )}

      {error && (
        <p className="banner banner--error" role="alert" data-testid="tenant-forgotten-error">
          {error}
        </p>
      )}

      {refused.length > 0 && (
        <ul className="freeze__effects" data-testid="tenant-forgotten-refused">
          {refused.map((record) => (
            <li key={record.kind}>{retainedLine(t, record)}</li>
          ))}
        </ul>
      )}

      {(phase.kind === "open" || phase.kind === "confirm" || phase.kind === "purging") &&
        (people.length === 0 ? (
          <p className="tenants__slug-status" data-testid="tenant-forgotten-none">
            {t("tenants.forgotten.none")}
          </p>
        ) : (
          <table className="table" data-testid="tenant-forgotten-table">
            <thead>
              <tr>
                <th scope="col">{t("tenants.forgotten.columnPerson")}</th>
                <th scope="col">{t("tenants.forgotten.columnForgottenAt")}</th>
                <th scope="col">{t("tenants.forgotten.columnState")}</th>
                <th scope="col">
                  <span className="visually-hidden">{t("tenants.forgotten.columnAction")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.userId} data-testid={`tenant-forgotten-row-${person.userId}`}>
                  <td>{person.pseudonym}</td>
                  <td>{formatDate(new Date(person.forgottenAt))}</td>
                  <td>
                    {rowState(person) === "purge" && t("tenants.forgotten.statePurgeable")}
                    {rowState(person) === "erasureIncomplete" && (
                      <span data-testid="tenant-forgotten-state-incomplete">
                        {t("tenants.forgotten.stateErasureIncomplete")}
                      </span>
                    )}
                    {rowState(person) === "retained" && (
                      <ul className="freeze__effects" data-testid="tenant-forgotten-state-retained">
                        {person.retained.map((record) => (
                          <li key={record.kind}>{retainedLine(t, record)}</li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    {person.purgeable && (
                      <button
                        type="button"
                        className="btn btn--danger"
                        disabled={phase.kind === "purging"}
                        onClick={() => setPhase({ kind: "confirm", person })}
                        data-testid={`tenant-forgotten-purge-${person.userId}`}
                      >
                        {t("tenants.forgotten.purge")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}

      {(phase.kind === "confirm" || phase.kind === "purging") && (
        <Modal
          role="alertdialog"
          className="freeze__dialog"
          labelledBy="tenant-forgotten-title"
          describedBy="tenant-forgotten-body"
          testId="tenant-forgotten-dialog"
          onClose={() => setPhase({ kind: "open" })}
          closeDisabled={phase.kind === "purging"}
          initialFocus={confirmRef}
        >
          <h2 id="tenant-forgotten-title">
            {t("tenants.forgotten.confirmTitle", { person: phase.person.pseudonym })}
          </h2>
          <ul id="tenant-forgotten-body" className="freeze__effects">
            {PURGE_EFFECTS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>

          <div className="modal__actions">
            <button
              type="button"
              className="btn"
              onClick={() => setPhase({ kind: "open" })}
              disabled={phase.kind === "purging"}
              data-testid="tenant-forgotten-cancel"
            >
              {t("tenants.cancel")}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              ref={confirmRef}
              onClick={() => onPurge(phase.person)}
              disabled={phase.kind === "purging"}
              data-testid="tenant-forgotten-submit"
            >
              {phase.kind === "purging"
                ? t("tenants.forgotten.purging")
                : t("tenants.forgotten.purge")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * One retained class as a sentence: its name, its row count, and — only when ADR-0020 §10 stamped one —
 * the day the obligation ends and the ground. A missing date is NOT rendered as "until null": an
 * unstamped record is one whose clock has not started, which §10 point 4 is explicit is not a licence to
 * delete either, so its line says the class and the count and stops there.
 *
 * WHICH of the three templates applies is `retainedLineKey`'s decision and not this function's — the
 * module owns every choice over a wire value — and the third one is the reason the choice had to move
 * there: the shared realm identity is not a retention hold at all, so it takes neither clock sentence and
 * neither a count nor a date is interpolated into it.
 */
function retainedLine(t: (key: string, opts?: Record<string, unknown>) => string, record: Retained) {
  const key = retainedLineKey(record);
  if (key === "tenants.forgotten.retainedSharedIdentity") {
    return t(key);
  }

  const name = t(retainedKindKey(record.kind));
  if (key === "tenants.forgotten.retainedUntil") {
    return t(key, {
      kind: name,
      rows: record.rows,
      date: formatDate(new Date(record.retainedUntil!)),
      ground: record.ground ?? t("tenants.forgotten.groundUnknown"),
    });
  }
  return t(key, { kind: name, rows: record.rows });
}
